/* FamilyExpiryBanner — shown on the Dashboard to Family Plan members
 * whose shared subscription is ending in <= 30 days. Dismissible for
 * the current day (reappears daily until the plan is renewed or the
 * user subscribes individually).
 *
 * Non-members and owners (who get their own renew nudge) see nothing.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";

const DISMISS_KEY = "rf_family_expiry_banner_dismissed_on";

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const t = new Date(iso).getTime();
    const now = Date.now();
    return Math.ceil((t - now) / 86400000);
  } catch {
    return null;
  }
}

export default function FamilyExpiryBanner() {
  const [membership, setMembership] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/family-plan/membership");
        setMembership(data?.membership || null);
      } catch { /* silent — Dashboard still renders */ }
    })();
    try {
      const today = new Date().toISOString().slice(0, 10);
      if (localStorage.getItem(DISMISS_KEY) === today) setDismissed(true);
    } catch { /* ignore quota */ }
  }, []);

  if (!membership || !membership.active) return null;
  const days = daysUntil(membership.expires_at);
  if (days == null || days > 30 || days < 0) return null;
  if (dismissed) return null;

  const prettyDate = (() => {
    try {
      return new Date(membership.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    } catch { return membership.expires_at; }
  })();

  const onDismiss = () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem(DISMISS_KEY, today);
    } catch { /* ignore quota */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="family-expiry-banner"
      className="mb-6 rounded-2xl bg-gradient-to-r from-rf-orange/15 to-rf-amber/15 border border-rf-orange/40 p-4 flex items-start gap-3"
    >
      <Users size={24} weight="duotone" className="text-rf-orange shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="font-display font-extrabold text-rf-ink">
          Your Family Plan ends on {prettyDate}
          {days > 1 ? ` — ${days} days left` : days === 1 ? " — 1 day left" : " — today"}.
        </p>
        <p className="text-xs text-rf-ink-soft mt-0.5 leading-relaxed">
          Every cookbook and recipe you&apos;ve made stays with your account. Pick an individual plan now and your subscription continues without a gap — nothing is deleted when the Family Plan ends.
        </p>
      </div>
      <Link
        to="/pricing?from=family_expiry"
        data-testid="family-expiry-banner-cta"
        className="btn-amber !py-2 !px-3 !text-sm shrink-0 whitespace-nowrap inline-flex items-center gap-1"
      >
        See plans <ArrowRight size={14} weight="bold" />
      </Link>
      <button
        type="button"
        data-testid="family-expiry-banner-dismiss"
        onClick={onDismiss}
        className="text-rf-ink-soft hover:text-rf-ink p-2 shrink-0"
        aria-label="Dismiss"
      >✕</button>
    </div>
  );
}
